import { onCLS, onFCP, onINP, onLCP, onTTFB, Metric } from 'web-vitals';
import { MetricRating, WebVitalsMetric, WebVitalsState } from '../types/vitals';

type VitalsListener = (state: WebVitalsState) => void;

let vitalsState: WebVitalsState = {
  lcp: null,
  inp: null,
  cls: null,
  fcp: null,
  ttfb: null,
  lastUpdated: Date.now(),
};

const listeners = new Set<VitalsListener>();
let isInitialized = false;

function notifyListeners() {
  vitalsState.lastUpdated = Date.now();
  listeners.forEach((listener) => {
    try {
      listener({ ...vitalsState });
    } catch (err) {
      console.error('Error in vitals listener:', err);
    }
  });
}

function handleMetric(metric: Metric) {
  const metricRecord: WebVitalsMetric = {
    name: metric.name,
    value: metric.value,
    rating: metric.rating as MetricRating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType,
    entries: metric.entries,
  };

  switch (metric.name) {
    case 'LCP':
      vitalsState.lcp = metricRecord;
      break;
    case 'INP':
      vitalsState.inp = metricRecord;
      break;
    case 'CLS':
      vitalsState.cls = metricRecord;
      break;
    case 'FCP':
      vitalsState.fcp = metricRecord;
      break;
    case 'TTFB':
      vitalsState.ttfb = metricRecord;
      break;
  }

  notifyListeners();
}

/**
 * Initializes Core Web Vitals telemetry tracking using Google's web-vitals library.
 */
export function initWebVitals(options?: { reportAllChanges?: boolean }): void {
  if (isInitialized || typeof window === 'undefined') {
    return;
  }
  isInitialized = true;

  try {
    onLCP(handleMetric, options);
    onINP(handleMetric, options);
    onCLS(handleMetric, options);
    onFCP(handleMetric, options);
    onTTFB(handleMetric, options);
  } catch (err) {
    console.warn('Web Vitals initialization warning:', err);
  }
}

/**
 * Get the current Web Vitals telemetry snapshot.
 */
export function getWebVitalsState(): WebVitalsState {
  return { ...vitalsState };
}

/**
 * Reset Web Vitals state (useful in testing or page transitions).
 */
export function resetWebVitalsState(): void {
  vitalsState = {
    lcp: null,
    inp: null,
    cls: null,
    fcp: null,
    ttfb: null,
    lastUpdated: Date.now(),
  };
  notifyListeners();
}

/**
 * Subscribe to real-time Web Vitals metric updates.
 */
export function subscribeToWebVitals(listener: VitalsListener): () => void {
  listeners.add(listener);
  listener({ ...vitalsState });
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Formats a metric value into a readable string with units.
 */
export function formatMetricValue(name: string, value: number): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '—';
  }

  switch (name) {
    case 'CLS':
      return value.toFixed(3);
    case 'LCP':
    case 'FCP':
      return value >= 1000 ? `${(value / 1000).toFixed(2)} с` : `${Math.round(value)} мс`;
    case 'INP':
    case 'FID':
    case 'TTFB':
      return `${Math.round(value)} мс`;
    default:
      return `${Math.round(value)}`;
  }
}

/**
 * Get human-readable rating in Ukrainian.
 */
export function getRatingLabel(rating: MetricRating | null | undefined): string {
  switch (rating) {
    case 'good':
      return 'Відмінно';
    case 'needs-improvement':
      return 'Потребує покращення';
    case 'poor':
      return 'Незадовільно';
    default:
      return 'Очікування даних';
  }
}

export const getMetricRatingLabel = getRatingLabel;

/**
 * Color classes according to Google Web Vitals threshold rating.
 */
export function getRatingColorClasses(rating: MetricRating | null | undefined): {
  text: string;
  bg: string;
  border: string;
  glow: string;
} {
  switch (rating) {
    case 'good':
      return {
        text: 'text-accent-emerald',
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/30',
        glow: 'shadow-[0_0_12px_rgba(16,185,129,0.3)]',
      };
    case 'needs-improvement':
      return {
        text: 'text-amber-400',
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/30',
        glow: 'shadow-[0_0_12px_rgba(245,158,11,0.3)]',
      };
    case 'poor':
      return {
        text: 'text-rose-400',
        bg: 'bg-rose-500/10',
        border: 'border-rose-500/30',
        glow: 'shadow-[0_0_12px_rgba(244,63,94,0.3)]',
      };
    default:
      return {
        text: 'text-text-muted',
        bg: 'bg-slate-800/50',
        border: 'border-slate-700/50',
        glow: '',
      };
  }
}

/**
 * Google official performance thresholds description.
 */
export function getMetricTargetInfo(name: string): {
  target: string;
  goodThreshold: string;
  poorThreshold: string;
  description: string;
} {
  switch (name) {
    case 'LCP':
      return {
        target: '≤ 2.5 с',
        goodThreshold: '≤ 2.5 с',
        poorThreshold: '> 4.0 с',
        description: 'Largest Contentful Paint — швидкість відображення основного візуального блоку.',
      };
    case 'INP':
      return {
        target: '≤ 200 мс',
        goodThreshold: '≤ 200 мс',
        poorThreshold: '> 500 мс',
        description: 'Interaction to Next Paint — швидкість реакції інтерфейсу на дії користувача.',
      };
    case 'CLS':
      return {
        target: '≤ 0.100',
        goodThreshold: '≤ 0.100',
        poorThreshold: '> 0.250',
        description: 'Cumulative Layout Shift — візуальна стабільність та відсутність зсувів елементів.',
      };
    case 'FCP':
      return {
        target: '≤ 1.8 с',
        goodThreshold: '≤ 1.8 с',
        poorThreshold: '> 3.0 с',
        description: 'First Contentful Paint — час появи першого контенту на екрані.',
      };
    case 'TTFB':
      return {
        target: '≤ 800 мс',
        goodThreshold: '≤ 800 мс',
        poorThreshold: '> 1800 мс',
        description: 'Time to First Byte — час відповіді сервера / Edge мережі Cloudflare.',
      };
    default:
      return {
        target: '—',
        goodThreshold: '—',
        poorThreshold: '—',
        description: '',
      };
  }
}

/**
 * Computes overall Web Vitals health rating across all reported metrics.
 */
export function getOverallRating(state: WebVitalsState): MetricRating {
  const metrics = [state.lcp, state.inp, state.cls, state.fcp, state.ttfb].filter(
    (m): m is WebVitalsMetric => m !== null
  );

  if (metrics.length === 0) {
    return 'good';
  }

  if (metrics.some((m) => m.rating === 'poor')) {
    return 'poor';
  }
  if (metrics.some((m) => m.rating === 'needs-improvement')) {
    return 'needs-improvement';
  }
  return 'good';
}
